import { ChatBubbleOutlined } from '@mui/icons-material';
import { Box, Chip, Divider, Stack } from '@mui/material';
import { format, isSameDay, isToday, isYesterday } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ChatMessageItem } from './chatMessageItem';
import Loader from 'components/v2/common/loader';
import Result from 'components/v2/common/Result';

import useTranslate from 'hooks/useTranslate';
import { RootState } from 'redux/store';
import type { ChatMessage, ChatReference } from 'types';

interface ChatMessageListProps {
    messages: ChatMessage[];
    isLoading: boolean;
    collapsed?: boolean;
    onDelete: (message: ChatMessage) => void;
    onReferenceClick: (reference: ChatReference) => void;
}

interface MessageGroup {
    date: string;
    messages: ChatMessage[];
}

const parseDate = (timestamp: string | null | undefined): Date | null => {
    if (!timestamp) {
        return null;
    }

    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateDivider = (timestamp: string, todayLabel: string, yesterdayLabel: string) => {
    const date = parseDate(timestamp);
    if (!date) {
        return '';
    }

    if (isToday(date)) {
        return todayLabel;
    }

    if (isYesterday(date)) {
        return yesterdayLabel;
    }

    return format(date, 'MMMM d, yyyy');
};

const groupMessagesByDate = (messages: ChatMessage[]): MessageGroup[] => {
    const groups: MessageGroup[] = [];

    messages.forEach((message) => {
        if (!message.created_at) {
            return;
        }

        const messageDate = parseDate(message.created_at);
        if (!messageDate) {
            return;
        }

        const lastGroup = groups[groups.length - 1];
        if (lastGroup) {
            const lastDate = parseDate(lastGroup.messages[0]?.created_at);
            if (lastDate && isSameDay(lastDate, messageDate)) {
                lastGroup.messages.push(message);
                return;
            }
        }

        groups.push({
            date: message.created_at,
            messages: [message],
        });
    });

    return groups;
};

const ChatMessageList = ({
    messages,
    isLoading,
    collapsed = false,
    onDelete,
    onReferenceClick,
}: ChatMessageListProps) => {
    const { translate } = useTranslate();
    const user = useSelector((state: RootState) => state.user.entity);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messageGroups = groupMessagesByDate(messages);
    const currentUserInitial = (
        user?.firstname?.[0] ||
        user?.email?.[0] ||
        translate('you')[0] ||
        'U'
    ).toUpperCase();

    useEffect(() => {
        if (isLoading || !messages.length) {
            return;
        }

        const container = messagesContainerRef.current;
        if (!container) {
            return;
        }

        const scrollToLatestMessage = () => {
            container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
        };

        const animationFrameId = window.requestAnimationFrame(scrollToLatestMessage);
        const fallbackTimeoutId = window.setTimeout(scrollToLatestMessage, 50);

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            window.clearTimeout(fallbackTimeoutId);
        };
    }, [isLoading, messages]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: collapsed ? 'none' : 'flex',
                    flex: '1 1 0px',
                    minHeight: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                <Loader isCircular />
            </Box>
        );
    }

    return (
        <Box
            ref={messagesContainerRef}
            sx={{
                display: collapsed ? 'none' : 'block',
                flex: '1 1 0px',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain',
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 1.5, sm: 2.5 },
                bgcolor: 'background.default',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                    width: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.400',
                    borderRadius: 4,
                },
            }}
        >
            {messageGroups.length === 0 ? (
                <Box
                    sx={{
                        minHeight: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Result
                        icon={<ChatBubbleOutlined sx={{ fontSize: 32 }} />}
                        title={translate('noMessagesYet')}
                        description={translate('noMessagesYetDescription')}
                        sx={{ width: '100%' }}
                    />
                </Box>
            ) : (
                <Stack spacing={2.5}>
                    {messageGroups.map((group) => (
                        <Box key={group.date}>
                            <Stack
                                direction="row"
                                spacing={1.5}
                                sx={{ alignItems: 'center', mb: 1.75 }}
                            >
                                <Divider sx={{ flex: 1 }} />
                                <Chip
                                    size="small"
                                    label={formatDateDivider(
                                        group.date,
                                        translate('today'),
                                        translate('yesterday')
                                    )}
                                    sx={{
                                        bgcolor: 'background.paper',
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                />
                                <Divider sx={{ flex: 1 }} />
                            </Stack>

                            <Stack spacing={1.5}>
                                {group.messages.map((message) => (
                                    <ChatMessageItem
                                        key={message.id}
                                        message={message}
                                        isOwn={message.user_id === user?.id}
                                        currentUserInitial={currentUserInitial}
                                        onDelete={onDelete}
                                        onReferenceClick={onReferenceClick}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default ChatMessageList;
